import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import type { FormEvent } from 'react'
import { useState } from 'react'
import {
  customerSessionQueryOptions,
  MIN_PASSWORD_LENGTH,
  useChangeCustomerPassword,
} from '@/entities/customer-session'
import { Button } from '@/shared/ui/button'
import { Card } from '@/shared/ui/card'
import { Field, Input } from '@/shared/ui/field'
import { Spinner } from '@/shared/ui/spinner'
import { customerProducts } from '../-lib/destination'
import { passwordErrorText } from '../-lib/messages'
import { ProductChoice } from '../-ui/product-choice'

// Смена собственного пароля заказчиком (21-hub-frontend § 3): стартовый пароль
// выдаёт сотрудник, и заменить его заказчик обязан уметь сам. Единственная
// не-GET операция, которую контур заказчика разрешает вне auth-ручек
// (00-architecture § 6).
export const Route = createFileRoute('/_public/customer/password')({
  loader: ({ context }) => context.queryClient.ensureQueryData(customerSessionQueryOptions),
  component: CustomerPasswordPage,
})

function CustomerPasswordPage() {
  const session = useQuery(customerSessionQueryOptions)
  const change = useChangeCustomerPassword()
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [repeat, setRepeat] = useState('')
  const [localError, setLocalError] = useState<string | undefined>(undefined)

  if (session.isLoading) {
    return (
      <Card>
        <div className="flex flex-col items-center gap-4 text-center">
          <Spinner label="Проверяем сессию" />
        </div>
      </Card>
    )
  }

  if (!session.data) {
    return (
      <Card title="Сессия истекла">
        <div className="flex flex-col gap-4">
          <p className="text-sm text-text-secondary">
            Чтобы сменить пароль, войдите в аккаунт заново.
          </p>
          <Link to="/login" search={{ notice: 'expired' }} className="self-start">
            <span className="text-sm text-accent">Войти</span>
          </Link>
        </div>
      </Card>
    )
  }

  if (change.isSuccess) {
    return (
      <Card title="Пароль изменён">
        <div className="flex flex-col gap-4">
          <p className="text-sm text-text-secondary">
            Новый пароль действует со следующего входа в сервисы.
          </p>
          <ProductChoice products={customerProducts(session.data.products)} />
        </div>
      </Card>
    )
  }

  function submit(e: FormEvent) {
    e.preventDefault()
    if (!current || !next) {
      setLocalError('Заполните все поля')
      return
    }
    if (next.length < MIN_PASSWORD_LENGTH) {
      setLocalError(`Новый пароль — минимум ${MIN_PASSWORD_LENGTH} символов`)
      return
    }
    if (next !== repeat) {
      setLocalError('Новый пароль и повтор не совпадают')
      return
    }
    if (next === current) {
      setLocalError('Новый пароль совпадает с текущим')
      return
    }
    setLocalError(undefined)
    change.mutate({ current, next })
  }

  const error = localError ?? (change.error ? passwordErrorText(change.error) : undefined)

  return (
    <Card title="Смена пароля">
      <form onSubmit={submit} className="flex flex-col gap-4">
        <p className="text-sm text-text-secondary">{session.data.fullName}</p>

        <Field label="Текущий пароль">
          {(props) => (
            <Input
              {...props}
              type="password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              autoComplete="current-password"
              disabled={change.isPending}
            />
          )}
        </Field>

        <Field label="Новый пароль" hint={`Минимум ${MIN_PASSWORD_LENGTH} символов`}>
          {(props) => (
            <Input
              {...props}
              type="password"
              value={next}
              onChange={(e) => setNext(e.target.value)}
              autoComplete="new-password"
              disabled={change.isPending}
            />
          )}
        </Field>

        <Field label="Повторите новый пароль">
          {(props) => (
            <Input
              {...props}
              type="password"
              value={repeat}
              onChange={(e) => setRepeat(e.target.value)}
              autoComplete="new-password"
              disabled={change.isPending}
            />
          )}
        </Field>

        {error && (
          <p role="alert" className="text-xs text-accent">
            {error}
          </p>
        )}

        <div className="flex items-center justify-between gap-2">
          <Link
            to="/customer/logout"
            className="text-xs text-text-tertiary hover:text-text-primary"
          >
            Выйти
          </Link>
          <Button type="submit" disabled={change.isPending}>
            {change.isPending ? 'Меняем…' : 'Сменить пароль'}
          </Button>
        </div>
      </form>
    </Card>
  )
}
